import { modal } from '@codeleap/modals'
import { View, Text, ActionIcon, SearchInput, Emoji } from '@/components'
import { theme, Chat } from '@/config'
import { emojis, searchEmojis } from '@packages/chat'
import { createStyles } from '@codeleap/styles'
import React, { useCallback, useState, useMemo } from 'react'
import { Channel } from 'stream-chat'
import { ChatLocalMesage } from '@packages/chat'

export type ReactionPickerModalProps = {
  message: ChatLocalMesage
  channel: Channel
}

const emojisPerRow = 7
const emojiSize = 32
const gap = 22

type EmojiItemProps = {
  item: typeof emojis[number] & { type?: string; title?: string }
  myReactionEmojiCode?: string | null
  onReactionPress: (emoji: string) => void
  isReactionPicker?: boolean
}

function EmojiItem({ item, myReactionEmojiCode, onReactionPress, isReactionPicker }: EmojiItemProps) {
  if (item.type === 'title') {
    return (
      <View style={styles.titleWrapper}>
        <Text
          text={item.title?.toUpperCase()}
          style={styles.titleText}
        />
      </View>
    )
  }

  const emoji = item?.emoji
  const isSelected = myReactionEmojiCode === emoji

  const onPress = () => onReactionPress(emoji)

  return (
    <Emoji
      isReactionPicker
      emoji={emoji}
      onPress={onPress}
      isSelected={isSelected}
      size={emojiSize}
      debugName={`ReactionPicker:${emoji}`}
      style={styles.emoji}
    />
  )
}

const MemoizedEmoji = React.memo(EmojiItem, () => true)

function formatEmojisWithTitles(_emojis = emojis) {
  const items: Array<any> = []
  let lastCategory: string | null = null

  for (const emoji of _emojis) {
    if (emoji.category !== lastCategory) {
      items.push({ type: 'title', title: emoji.category })
      lastCategory = emoji.category
    }
    items.push(emoji)
  }

  return items
}

export const ReactionPickerModal = modal<ReactionPickerModalProps>({ id: 'reaction-picker-modal' }).content((props) => {
  const { message, channel, toggle } = props

  const [search, setSearch] = useState('')
  const hasSearch = search.trim().length > 0
  const myReaction = message?.own_reactions?.[0]
  const myReactionEmojiCode = myReaction?.emoji_code

  const visibleRows = useMemo(() => {
    if (!hasSearch) {
      return formatEmojisWithTitles(emojis)
    }

    const filtered = searchEmojis(search)
    return formatEmojisWithTitles(filtered)
  }, [search, hasSearch])

  const handleReactionPress = useCallback(async (emoji: string) => {
    await Chat.Services.handleReaction({
      message,
      channel,
      emoji,
      onReaction: () => {
        toggle()
      },
    })
    Promise.resolve().then(() => {
      const { APIClient } = require('@/services')
      APIClient.Chat.invalidateReactionsCache(message.id)
    })
  }, [message, channel, toggle])

  ReactionPickerModal.useProps({
    showClose: false,
    style: ['centered', 'box:responsiveness', {
      box: styles.box,
    }],
  })

  return (
    <View style={styles.emojiContainer}>
      <View style={styles.header}>
        <View style={['fullWidth', 'alignCenter', 'justifySpaceBetween', 'mb:2']}>
          <View style={{ width: 24, height: 24 }} />

          <Text
            text='React'
            style={['semiBold', 'p2', { color: theme.colors.textHeadingRegular }]}
          />

          <ActionIcon
            icon='x'
            onPress={toggle}
            style={['normalize', 'iconSize:3', 'color:textHeadingRegular']}
          />
        </View>
        <SearchInput
          placeholder='Search emoji'
          onSearchChange={setSearch}
          debounce={300}
          onClear={() => setSearch('')}
          style={['height:largeish']}
        />
      </View>
      <View style={styles.scrollContainer}>
        <View style={styles.emojiGrid}>
          {visibleRows.map((item, idx) => {
            const key = item.emoji || `title-${item.title}-${idx}`

            return (
              <MemoizedEmoji
                key={key}
                item={item}
                myReactionEmojiCode={myReactionEmojiCode}
                onReactionPress={handleReactionPress}
                isReactionPicker
              />
            )
          })}
        </View>
      </View>
    </View>
  )
})

const styles = createStyles((theme) => ({
  titleWrapper: {
    width: '100%',
    paddingTop: theme.spacing.value(2),
    paddingBottom: theme.spacing.value(1),
    gridColumn: '1 / -1',
  },
  titleText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textCaption,
  },
  emoji: {
    width: emojiSize,
    height: emojiSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius.small,
    marginBottom: theme.spacing.value(1),
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 16,
    paddingLeft: 16,
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing.value(1),
    maxHeight: 250,
    minHeight: 0,
    justifyContent: 'center',
  },
  emojiGrid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${emojisPerRow}, ${emojiSize}px)`,
    gap: `4px ${gap}px`,
    paddingBottom: theme.spacing.value(2),
  },
  box: { width: '100%', maxWidth: 402, height: '100%', maxHeight: 386, paddingRight: 0, paddingLeft: 0 },
  emojiContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
}))
